import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  gray: '#64748b',
  lightGray: '#f1f5f9',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

// Styled Components
const Container = styled.div`
  padding: 0.7rem;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: ${COLORS.primary};
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  background: ${COLORS.white};
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 150px;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  color: ${COLORS.gray};
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const FilterSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: ${COLORS.white};
  color: ${COLORS.primary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterInput = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: ${COLORS.white};
  color: ${COLORS.primary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
  max-width: 100%;
  
  @media (max-width: 1200px) {
    overflow-x: scroll;
  }
  
  @media (max-width: 640px) {
    overflow-x: visible;
  }
`;

const BookingsTable = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  background: ${COLORS.white};
  
  @media (max-width: 1024px) {
    font-size: 0.875rem;
    min-width: 700px;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    min-width: 600px;
  }
  
  @media (max-width: 640px) {
    display: block;
    min-width: auto;
    
    thead, tbody, th, td, tr {
      display: block;
    }
    
    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }
    
    tr {
      border: 1px solid ${COLORS.lightGray};
      margin-bottom: 1rem;
      border-radius: 0.5rem;
      padding: 0.5rem;
    }
    
    td {
      border: none;
      border-bottom: 1px solid ${COLORS.lightGray};
      position: relative;
      padding-left: 50% !important;
      
      &:before {
        position: absolute;
        top: 6px;
        left: 6px;
        width: 45%;
        padding-right: 10px;
        white-space: nowrap;
        font-weight: 600;
        color: ${COLORS.primary};
        text-align: left;
      }
    }
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 0.5rem;
  background: ${COLORS.lightGray};
  color: ${COLORS.primary};
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:hover {
    background: ${COLORS.lightGray};
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid ${COLORS.lightGray};
  color: ${COLORS.primary};
  
  @media (max-width: 640px) {
    &:nth-of-type(1):before { content: "Guest"; }
    &:nth-of-type(2):before { content: "Room"; }
    &:nth-of-type(3):before { content: "Dates"; }
    &:nth-of-type(4):before { content: "Contact"; }
    &:nth-of-type(5):before { content: "Special Req"; }
    &:nth-of-type(6):before { content: "Status"; }
    &:nth-of-type(7):before { content: "Actions"; }
  }
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${COLORS.lightGray};
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${COLORS.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${COLORS.gray};
  padding: 0.5rem;
  border-radius: 0.5rem;
  
  &:hover {
    background: ${COLORS.lightGray};
  }
`;

const ModalSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ModalLabel = styled.h3`
  font-size: 1rem;
  color: ${COLORS.primary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ModalText = styled.p`
  color: ${COLORS.secondary};
  line-height: 1.6;
  margin: 0;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #e0e7ff;
  color: ${COLORS.accent};
  
  &:hover {
    background-color: #c7d2fe;
    transform: scale(1.05);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'confirmed':
        return '#dcfce7';
      case 'pending':
        return '#fef9c3';
      case 'completed':
        return '#e0e7ff';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'confirmed':
        return '#166534';
      case 'pending':
        return '#854d0e';
      case 'completed':
        return '#3730a3';
      default:
        return '#374151';
    }
  }};
`;

const StatusSelect = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: ${COLORS.white};
  color: ${COLORS.primary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.delete {
    background-color: #fee2e2;
    color: ${COLORS.danger};
    
    &:hover {
      background-color: #fecaca;
      transform: scale(1.05);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  background: ${props => props.active ? COLORS.accent : COLORS.white};
  color: ${props => props.active ? COLORS.white : COLORS.primary};
  cursor: pointer;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:first-child {
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
  }
  
  &:last-child {
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? COLORS.accent : COLORS.lightGray};
  }
  
  &:disabled {
    background: ${COLORS.lightGray};
    color: ${COLORS.gray};
    cursor: not-allowed;
  }
`;

const NoBookingsMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${COLORS.gray};
  background: ${COLORS.white};
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
`;

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const bookingsPerPage = 10;

  // Helper function to get room name by ID
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? (room.name || room.type || room.id) : roomId;
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const bookingsCollection = collection(db, 'bookings');
        const bookingsQuery = query(bookingsCollection, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(bookingsQuery);
        
        const bookingsData = snapshot.docs.map(doc => {
          const data = doc.data();
        
          return {
            id: doc.id,
            ...data,
            checkInDate: data.checkInDate,
            checkOutDate: data.checkOutDate,
            createdAt: data.createdAt
          };
        });
        
        setBookings(bookingsData);
        setFilteredBookings(bookingsData);
        
        // Extract unique room types
        const uniqueRoomTypes = [...new Set(bookingsData.map(booking => booking.roomType))];
        setRoomTypes(uniqueRoomTypes);
        
        // Fetch rooms for name mapping
        const roomsCollection = collection(db, 'rooms');
        const roomsSnapshot = await getDocs(roomsCollection);
        const roomsData = roomsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRooms(roomsData);
        
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookings();
  }, []);

  const filterBookings = useCallback(() => {
    let filtered = [...bookings];
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }
    
    // Filter by room type
    if (roomTypeFilter !== 'all') {
      filtered = filtered.filter(booking => booking.roomType === roomTypeFilter);
    }
    
    // Filter by date
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filterDate.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(booking => {
        const checkInDate = booking.checkInDate?.toDate ? 
          booking.checkInDate.toDate() : 
          new Date(booking.checkInDate);
          
        const checkOutDate = booking.checkOutDate?.toDate ? 
          booking.checkOutDate.toDate() : 
          new Date(booking.checkOutDate);
          
        checkInDate.setHours(0, 0, 0, 0);
        checkOutDate.setHours(0, 0, 0, 0);
        
        return checkInDate <= filterDate && checkOutDate >= filterDate;
      });
    }
    
    setFilteredBookings(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [bookings, statusFilter, roomTypeFilter, dateFilter]);

  useEffect(() => {
    filterBookings();
  }, [statusFilter, roomTypeFilter, dateFilter, bookings, filterBookings]);

  const handleDeleteBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'bookings', bookingId));
        
        // Update local state
        setBookings(prevBookings => prevBookings.filter(booking => booking.id !== bookingId));
        setFilteredBookings(prevBookings => prevBookings.filter(booking => booking.id !== bookingId));
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: newStatus
      });
      
      // Update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId ? { ...booking, status: newStatus } : booking
        )
      );
      
      // Update selectedBooking if it's currently open in modal
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Pagination
  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Container>
        <Title>Bookings Management</Title>
        
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Room Type</FilterLabel>
            <FilterSelect 
              value={roomTypeFilter} 
              onChange={(e) => setRoomTypeFilter(e.target.value)}
            >
              <option value="all">All Room Types</option>
              {roomTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Date (Check-in/Check-out)</FilterLabel>
            <FilterInput 
              type="date" 
              value={dateFilter} 
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </FilterGroup>
        </FiltersContainer>
        
        {loading ? (
          <div>Loading bookings...</div>
        ) : filteredBookings.length > 0 ? (
          <>
            <TableWrapper>
              <BookingsTable>
                <thead>
                  <tr>
                    <TableHeader style={{ width: '18%' }}>Guest</TableHeader>
                    <TableHeader style={{ width: '15%' }}>Room</TableHeader>
                    <TableHeader style={{ width: '25%' }}>CheckIn/CheckOut</TableHeader>
                    <TableHeader style={{ width: '20%' }}>Contact</TableHeader>
                    <TableHeader style={{ width: '15%' }}>Special Req</TableHeader>
                    <TableHeader style={{ width: '7%' }}>Status</TableHeader>
                    <TableHeader style={{ width: '5%' }}>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {currentBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div style={{ fontWeight: '500' }}>
                          {booking.name.length > 20 ? booking.name.substring(0, 20) + '...' : booking.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: '500' }}>
                          {getRoomName(booking.roomType)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontSize: '0.875rem' }}>
                          <div>{formatDate(booking.checkInDate)}</div>
                          <div style={{ color: COLORS.gray, fontSize: '0.75rem' }}>
                            to {formatDate(booking.checkOutDate)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontSize: '0.875rem' }}>
                          <div>{booking.email}</div>
                          <div style={{ color: COLORS.gray, fontSize: '0.75rem' }}>{booking.phone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: COLORS.gray,
                          maxWidth: '120px',
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {(booking.message || booking.specialRequirements || booking.requirements || booking.notes) || 'None'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={booking.status || 'pending'}>
                          {(booking.status || 'pending').charAt(0).toUpperCase() + (booking.status || 'pending').slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexDirection: 'column' }}>
                          <ViewButton 
                            onClick={() => handleViewBooking(booking)}
                            title="View booking details"
                            style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                          >
                            👁️
                          </ViewButton>
                          <ActionButton 
                            className="delete"
                            onClick={() => handleDeleteBooking(booking.id)}
                            title="Delete booking"
                            style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                          >
                            🗑️
                          </ActionButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </BookingsTable>
            </TableWrapper>
            
            <PaginationContainer>
              <PaginationButton 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PaginationButton>
              
              {[...Array(totalPages).keys()].map(number => (
                <PaginationButton
                  key={number + 1}
                  active={currentPage === number + 1}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </PaginationButton>
              ))}
              
              <PaginationButton
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PaginationButton>
            </PaginationContainer>
          </>
        ) : (
          <NoBookingsMessage>
            No bookings found matching the selected filters.
          </NoBookingsMessage>
        )}
        
        {showModal && selectedBooking && (
          <ModalOverlay onClick={handleCloseModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Booking Details</ModalTitle>
                <CloseButton onClick={handleCloseModal}>×</CloseButton>
              </ModalHeader>
              
              <ModalSection>
                <ModalLabel>Guest Information</ModalLabel>
                <ModalText><strong>Name:</strong> {selectedBooking.name}</ModalText>
                <ModalText><strong>Email:</strong> {selectedBooking.email}</ModalText>
                <ModalText><strong>Phone:</strong> {selectedBooking.phone}</ModalText>
              </ModalSection>
              
              <ModalSection>
                <ModalLabel>Booking Information</ModalLabel>
                <ModalText><strong>Room Type:</strong> {getRoomName(selectedBooking.roomType)}</ModalText>
                <ModalText><strong>Check-in:</strong> {formatDate(selectedBooking.checkInDate)}</ModalText>
                <ModalText><strong>Check-out:</strong> {formatDate(selectedBooking.checkOutDate)}</ModalText>
                <ModalText><strong>Status:</strong> {selectedBooking.status || 'pending'}</ModalText>
                <ModalText><strong>Booking Created:</strong> {formatDate(selectedBooking.createdAt)}</ModalText>
              </ModalSection>
              
              <ModalSection>
                <ModalLabel>Change Status</ModalLabel>
                <StatusSelect
                  value={selectedBooking.status || 'pending'}
                  onChange={(e) => handleStatusChange(selectedBooking.id, e.target.value)}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </StatusSelect>
              </ModalSection>
              
              <ModalSection>
                <ModalLabel>Special Requirements</ModalLabel>
                <ModalText>
                  {(selectedBooking.message || selectedBooking.specialRequirements || selectedBooking.requirements || selectedBooking.notes) || 'No special requirements'}
                </ModalText>
              </ModalSection>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </>
  );
};

export default BookingsManagement;
